import { findLastIndex } from "lodash-es";
import { createSelector } from "reselect";
import { createReducer } from "typesafe-actions";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";

import EditActivityFocusableField from "../../component/activity/type/EditActivityFocusableField";
import { RootState } from "../rootReducer";

import {
  Actions,
  clearEntityViewReturnState,
  hideEntityView,
  registerEntityPreview,
  setEntityTypesWithExternalPreview,
  showEntityView,
  unregisterEntityPreview,
} from "./actions";
import ActiveEntityPreview from "./ActiveEntityPreview";
import { EntityViewParentEntity, EntityViewSource } from "./EntityViewPayload";
import RecordPaneReturnState from "./RecordPaneReturnState";

export interface EntityViewState {
  // What is mounted, reported by the panes. Every other field here is the last request to *open* a
  // preview, which is a different thing — merging the two is what ENG-1815 fixed.
  activeEntityPreviews: ActiveEntityPreview[];
  edit?: boolean;
  editValues?: Partial<AnyEntity>;
  elementId?: string;
  entityId?: AnyEntity["id"];
  entityType?: EntityType;
  entityTypesWithExternalPreview: EntityType[];
  focusedFieldName?: EditActivityFocusableField;
  parentEntity?: EntityViewParentEntity;
  requiredFieldsOnly?: boolean;
  returnState?: RecordPaneReturnState;
  source?: EntityViewSource;
  zIndex?: number;
}

const initialState: EntityViewState = {
  activeEntityPreviews: [],
  entityTypesWithExternalPreview: [],
};

const entityView = createReducer<EntityViewState, Actions>(initialState)
  .handleAction(
    showEntityView,
    (
      state,
      {
        payload: {
          edit,
          editValues,
          elementId,
          entityId,
          entityType,
          focusedFieldName,
          parentEntity,
          requiredFieldsOnly,
          returnState,
          source,
          zIndex,
        },
      }
    ) => ({
      ...state,
      edit,
      editValues,
      elementId,
      entityId,
      entityType,
      focusedFieldName,
      parentEntity,
      requiredFieldsOnly,
      returnState,
      source,
      zIndex,
    })
  )
  .handleAction(hideEntityView, (state) => ({
    ...state,
    editValues: undefined,
    entityId: undefined,
    entityType: undefined,
    focusedFieldName: undefined,
    parentEntity: undefined,
    requiredFieldsOnly: undefined,
    returnState: undefined,
  }))
  .handleAction(clearEntityViewReturnState, (state) => ({ ...state, returnState: undefined }))
  .handleAction(registerEntityPreview, (state, { payload }) => ({
    ...state,
    activeEntityPreviews: [...state.activeEntityPreviews, payload],
  }))
  .handleAction(setEntityTypesWithExternalPreview, (state, { payload }) => ({
    ...state,
    entityTypesWithExternalPreview: payload,
  }))
  .handleAction(unregisterEntityPreview, (state, { payload }) => {
    // Drops one entry, not every match. Panes hand a record from one host to another by unmounting
    // the old pane and mounting the new one, and if those ever land in the order mount-then-unmount,
    // dropping every match would empty the list while the preview is still on screen.
    const index = findLastIndex(
      state.activeEntityPreviews,
      ({ entityId, entityType }) =>
        entityId === payload.entityId && entityType === payload.entityType
    );
    if (index < 0) {
      return state;
    }
    return { ...state, activeEntityPreviews: state.activeEntityPreviews.toSpliced(index, 1) };
  });

const entityViewState = (state: RootState) => state.entityView;

export const getEntityView = createSelector(entityViewState, (entityView) => entityView);

export const getEntityViewEntityType = createSelector(
  entityViewState,
  ({ entityType }) => entityType
);

export const getEntityViewEntityId = createSelector(entityViewState, ({ entityId }) => entityId);

export const isEntityViewVisible = createSelector(
  entityViewState,
  ({ entityId, entityType }) => !!entityId && !!entityType
);

export const shouldEditPreviewedEntity = createSelector(entityViewState, ({ edit }) => edit);

export const getEditValues = createSelector(entityViewState, ({ editValues }) => editValues);

export const shouldEditRequiredFieldsOnly = createSelector(
  entityViewState,
  ({ requiredFieldsOnly }) => !!requiredFieldsOnly
);

export const getPreviewPaneZIndex = createSelector(entityViewState, ({ zIndex }) => zIndex);

export const getEntityViewElement = createSelector(entityViewState, ({ elementId }) => elementId);

export const getEntityTypesWithExternalPreview = createSelector(
  entityViewState,
  ({ entityTypesWithExternalPreview }) => entityTypesWithExternalPreview
);

export const getEntityViewParentEntity = createSelector(
  entityViewState,
  ({ parentEntity }) => parentEntity
);

export const getEntityViewReturnState = createSelector(
  entityViewState,
  ({ returnState }) => returnState
);

export const getFocusedFieldName = createSelector(
  entityViewState,
  ({ focusedFieldName }) => focusedFieldName
);

export const getEntityViewSource = createSelector(entityViewState, ({ source }) => source);

export const getActiveEntityPreviews = createSelector(
  entityViewState,
  ({ activeEntityPreviews }) => activeEntityPreviews
);

export const getLastActiveEntityPreview = createSelector(
  getActiveEntityPreviews,
  (previews) => previews[previews.length - 1]
);

export type EntityViewActions = Actions;

export default entityView;
